import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { WhatsAppContact, WhatsAppIntakeOption } from '../../types/whatsapp';

interface AssociateIntakeDialogProps {
  open: boolean;
  /** Búsqueda en backend; si el contacto tiene empresa se filtra por ella. */
  searchIntakes: (search: string, accountId?: string | null) => Promise<WhatsAppIntakeOption[]>;
  contact: WhatsAppContact | null;
  submitting?: boolean;
  onClose: () => void;
  onAssociate: (intake: WhatsAppIntakeOption) => void | Promise<void>;
}

const SEARCH_DEBOUNCE_MS = 300;

const AssociateIntakeDialog: React.FC<AssociateIntakeDialogProps> = ({
  open,
  searchIntakes,
  contact,
  submitting = false,
  onClose,
  onAssociate,
}) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState('');
  const [intakes, setIntakes] = useState<WhatsAppIntakeOption[]>([]);
  const [loading, setLoading] = useState(false);

  const companyId = contact?.companyId ?? null;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const result = await searchIntakes(search, companyId);
        if (!cancelled) setIntakes(result);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudieron buscar Intakes.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // searchIntakes se recrea en cada render del padre; solo se busca al cambiar filtros.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, companyId]);

  const reset = () => {
    setSearch('');
    setSelectedId('');
    setError('');
    setIntakes([]);
  };

  const handleAssociate = () => {
    setError('');
    const intake = intakes.find((i) => i.id === selectedId);
    if (!intake) {
      setError('Selecciona un Intake.');
      return;
    }
    if (contact?.companyId && contact.companyId !== intake.companyId) {
      setError(
        `No se puede asociar: el contacto pertenece a "${contact.companyName}" y el Intake a "${intake.companyName}".`
      );
      return;
    }
    void onAssociate(intake);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Asociar Intake</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {contact?.companyId && (
            <Alert severity="info">
              Contacto con empresa: <strong>{contact.companyName}</strong>. Solo se permiten Intakes de
              esa empresa.
            </Alert>
          )}
          {!contact?.companyId && (
            <Alert severity="info">
              Contacto en <strong>Sin empresa</strong>: al asociar, pasará a la empresa del Intake.
            </Alert>
          )}
          <TextField
            size="small"
            fullWidth
            label="Buscar Intake (número, nombre, teléfono o correo)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 280, overflow: 'auto' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {!loading &&
              intakes.map((i) => {
                const blocked = Boolean(contact?.companyId && contact.companyId !== i.companyId);
                return (
                  <ListItemButton
                    key={i.id}
                    selected={selectedId === i.id}
                    disabled={blocked}
                    onClick={() => setSelectedId(i.id)}
                  >
                    <ListItemText
                      primary={`${i.label}${i.employeeName ? ` · ${i.employeeName}` : ''}`}
                      secondary={`${i.companyName}${i.phoneHint ? ` · ${i.phoneHint}` : ''}${
                        blocked ? ' · Empresa distinta al contacto' : ''
                      }`}
                    />
                  </ListItemButton>
                );
              })}
            {!loading && intakes.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No se encontraron Intakes.
                </Typography>
              </Box>
            )}
          </List>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleAssociate} disabled={submitting} sx={{ textTransform: 'none' }}>
          Asociar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssociateIntakeDialog;
